import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import mime from 'mime-types';
import classnames from 'classnames';
import AnimatedUploadingFilePopUp from '../AnimatedUploadingFilePopUp';
import FileIcon from './FileIcon';
import Thumbnail from '../Thumbnail';
import FilePreview from '../FilePreview';
import InteractionsMenu from './InteractionsMenu';
import {getStep12Title} from '../../../modules/Inspection/utils/getStep12Title';
import styles from './styles.module.scss';

const UploadedFile = ({
                        fileName,
                        fileTypeName,
                        fileTypeID,
                        orderFileTypeID,
                        fileExtension,
                        fileID,
                        stepID,
                        containerClassName = '',
                        getFile,
                        getFileFromOrder,
                        onFileDelete,
                        freeText,
                        freeID,
                        shumaId,
                        orderID,
                        InteractionsMenuComponent = InteractionsMenu,
                        showGlobalErrorMessage,
                        shumaTypeID,
                        thumbnail,
                        isFileFromOrder
                      }) => {
  const [content, setContent] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const openPreview = () => setPreviewIsOpen(true);
  const closePreview = () => setPreviewIsOpen(false);

  const updateFile = async () => {
    try {
      setFileType(null);
      setContent(null);
      const body = isFileFromOrder
        ? await getFileFromOrder({orderID, fileID})
        : await getFile({shumaId, fileID});
      const mimeType = mime.lookup(fileExtension);
      const dataUri = `data:${mimeType};base64,${body}`;
      setContent(dataUri);
      setFileType(getType());
      return dataUri;
    } catch (e) {
      setFileType(null);
      showGlobalErrorMessage(e.message)
    }
  };

  const handleFileOpen = () => {
    if (getType()) {
      Promise.resolve()
        .then(() => setLoading(true))
        .then(() => setFileType('spinner'))
        .then(updateFile)
        .then(() => openPreview())
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {setFileType(getType());}, [fileID]);

  const getType = () => {
    switch (mime.lookup(fileExtension)) {
      case 'application/pdf':
        return 'pdf';
      case 'image/gif':
      case 'image/jpeg':
      case 'image/jpg':
      case 'image/tiff':
      case 'image/png':
        return 'image';
      default:
        return null;
    }
  };

  const steps = [
    {
      id: 1,
      topic: 'פרטי הנכס',
    },
    {
      id: 6,
      topic: 'המצב התכנוני משפטי והקנייני',
    },
    {
      id: 3,
      topic: 'ביקור בנכס',
    },
    {
      id: 4,
      topic: 'תיאור הסביבה',
    },
    {
      id: 5,
      topic: 'תיאור הנכס',
    },
    {
      id: 8,
      topic: 'עקרונות גורמים ושיקולים',
    },
    {
      id: 9,
      topic: 'נכסי השוואה',
    },
    {
      id: 10,
      topic: 'תחשיב',
    },
    {
      id: 12,
      topic: getStep12Title(shumaTypeID) || 'בטוחה בנקאית',
    },
    {
      id: 11,
      topic: 'הפקת דוח ומסמכים',
    },
  ];
  const stepData = steps.filter(item => item.id === stepID);
  const stepName = stepData[0]?.topic ? stepData[0].topic : '';

  return (
    <div className={classnames(styles.uploadedFile, containerClassName)}>
      <div className={styles.headerContainer}>
        <div className={classnames(
          styles.sticker,
          isFileFromOrder ? styles.stickerOrder : styles.stickerReport
        )}>
          {isFileFromOrder ? 'מהזמנה' : 'מוצג בדוח הסופי'}
        </div>
        <InteractionsMenuComponent
          fileTypeID={fileTypeID}
          orderFileTypeID={orderFileTypeID}
          fileExtension={fileExtension}
          mimeType={mime.lookup(fileExtension)}
          fileID={fileID}
          stepID={stepID}
          shumaId={shumaId}
          orderID={orderID}
          content={content}
          fileName={fileName}
          onFileDelete={onFileDelete}
          editFormValues={{freeID, freeText}}
          updateFile={updateFile}
          isFileFromOrder={isFileFromOrder}
        />
      </div>
      <div onClick={handleFileOpen} className={styles.imageContainer}>
        {thumbnail
          ? <Thumbnail uri={thumbnail} fileExtension={fileExtension}/>
          : <FileIcon fileExtension={fileExtension}/>
        }
      </div>
      <div className={styles.fileInfo}>
        <div className={styles.fileName}>
          {fileName}
        </div>
        <div className={styles.fileType}>
          <span>סוג קובץ:</span>
          {fileTypeName}
        </div>
        {stepName && !isFileFromOrder && (
          <div className={styles.fileAssociation}>
            <span>מסך שיוך הקובץ:</span>
            {stepName}
          </div>
        )}
        {(fileTypeID === 11) && !isFileFromOrder &&
          <div className={styles.freeTextItem}>
            <span>כותרת התמונה:</span>
            <div className={styles.freeText}>{freeText}</div>
          </div>
        }
      </div>
      <AnimatedUploadingFilePopUp show={loading}/>
      <FilePreview
        isOpen={previewIsOpen}
        close={closePreview}
        dataURI={content}
        type={fileType}
      />
    </div>
  );
};

UploadedFile.propTypes = {
  fileID: PropTypes.number.isRequired,
  stepID: PropTypes.number,
  getFile: PropTypes.func,
  onFileDelete: PropTypes.func,
  fileName: PropTypes.string,
  fileTypeName: PropTypes.string,
  fileTypeID: PropTypes.number,
  fileExtension: PropTypes.string,
  containerClassName: PropTypes.string,
  freeText: PropTypes.string,
  freeID: PropTypes.number,
  shumaId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  InteractionsMenuComponent: PropTypes.node,
  showGlobalErrorMessage: PropTypes.func,
  isFileFromOrder: PropTypes.bool,
};

export default UploadedFile;
